import { Injectable } from '@angular/core';
import {
    SymplifyItem,
    SymplifyMetaData,
} from '../../../../projects/shared-lib/src/lib/models/symplify-item';
import { HelpsService } from './helps.service';
import { PriceWeb } from '../../../../projects/shared-lib/src/lib/models/price-web';
import { CartPrice } from '../../../../projects/shared-lib/src/lib/models/cart-price';
import { lastValueFrom } from 'rxjs';
import { Cart } from '../../../../projects/shared-lib/src/lib/models/cart';
import { environment } from '../../../../environments';
import { ClientDetails } from '../../models/client-details';

declare const carma: any;

@Injectable({
    providedIn: 'root',
})
export class SymplifyCartService {
    client: ClientDetails;
    active = false;
    webInteractionsActive = false;
    private readonly TRACKING_SERVER_ID = 'YOUR_SERVER_ID'; // À remplacer par l'ID du serveur Symplify
    private readonly TRACKING_TOKEN = 'YOUR_WEB_INTERACTION_TOKEN'; // À remplacer par le token Web Interaction

    constructor() {}

    async init(lang, active = false) {
        this.active = active;
        if (!this.active) {
            return;
        }



        // Script panier abandonné (historique)
        let id = '';
        if (environment.systemValues === 'tkp') {
            if (environment.production) {
                id = 'www5_8mGXr';
                if (lang === 'fr') {
                    id = 'www5_Sq4lB';
                }
            } else {
                id = 'www5_rQ3UA';
                if (lang === 'fr') {
                    id = 'www5_6TpAL';
                }
            }
        }
        await lastValueFrom(
            HelpsService.injectScriptObservable(
                '//mimgs.s3.amazonaws.com/scripts/ac.js',
                { id: 'carma_ac', 'data-cid': String(id) },
            ),
        );
    }

    async initWebTracking(lang, active = false) {
        this.webInteractionsActive = active;
        if (!this.webInteractionsActive) {
            return;
        }

        // Injecter le script de configuration Web Interactions
        try {
            const configScript = document.createElement('script');
            configScript.type = 'text/javascript';
            configScript.textContent = `
                var carma = carma || {};
                carma.tracking = carma.tracking || {};
                carma.tracking.server = "${this.TRACKING_SERVER_ID}";
                carma.tracking.token = "${this.TRACKING_TOKEN}";
            `;
            document.head.appendChild(configScript);

            await HelpsService.injectScript(
                'https://cdn.carmamarketing.com/tracking/carma.tracking.min.js',
            );
            this.webInteractionsActive = true;
        } catch (error) {
            console.error(
                'Failed to initialize Symplify Web Interactions',
                error,
            );
        }
    }

    // --- Fonctions Web Interactions Symplify ---
    track(eventName: string, eventData?: any) {
        if (!this.webInteractionsActive) {
            return;
        }
        try {
            if (
                carma &&
                carma.tracking &&
                typeof carma.tracking.track === 'function'
            ) {
                if (eventData) {
                    carma.tracking.track(eventName, eventData);
                } else {
                    carma.tracking.track(eventName);
                }
            }
        } catch (e) {}
    }

    identifyUser(user: any) {
        if (!this.webInteractionsActive) {
            return;
        }
        try {
            if (
                carma &&
                carma.tracking &&
                typeof carma.tracking.identify === 'function'
            ) {
                carma.tracking.identify(user);
            }
        } catch (e) {}
    }

    trackVisitedPage(page: string) {
        this.track('visited-page', { page });
    }

    trackBrowsedCategory(category: string) {
        this.track('browsed-category', { category });
    }

    // --- Fonctions historiques panier abandonné (inchangées) ---
    addAllCart(cart: Cart) {
        if (!this.active) {
            return;
        }
        try {
            if (cart) {
                cart?.cartElements.forEach((elm) => {
                    this.addToCartPrice(elm.prices);
                });
            }
        } catch (e) {
            console.log(e);
        }
    }

    registerEmail(email) {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.registerEmail(email);
            this.identifyUser(email);
        } catch (e) {
            console.log(e);
        }
    }

    addMetaData(metaData: SymplifyMetaData) {
        if (!this.active) {
            return;
        }
        try {
            for (const [key, value] of Object.entries(metaData)) {
                const t = {};
                t[key] = value;
                carma.ac.addMetaData(t);
            }
        } catch (e) {
            console.log(e);
        }
    }

    addToCart(item: SymplifyItem) {
        if (!this.client || !this.active) {
            return;
        }
        try {
            carma.ac.addToCart(item);
        } catch (e) {
            console.log(e);
        }
    }

    addToCartPrice(priceWebs: PriceWeb[] | CartPrice[] | any[]) {
        if (!(environment.systemValues === 'tkp')) {
            return;
        }
        if (!this.client || !this.active) {
            return;
        }
        try {
            let metaData: SymplifyMetaData = {};
            priceWebs.forEach((value) => {
                if (value.abandonedCartActive) {
                    if (
                        value.pageUrl &&
                        value.pageUrl.length > 10 &&
                        value.title
                    ) {
                        const item: SymplifyItem = {
                            id: String(value.id),
                            amount:
                                value.qty *
                                ((value.price + value.serviceCharges) / 100),
                            quantity: value.qty,
                            description: value.name,
                            name: value.name,
                        };
                        this.addToCart(item);
                        metaData = {
                            image_url: value.imageUrl,
                            sale_url: this.addOrReplaceUrlParam(
                                value.cleanPageUrl
                                    ? value.cleanPageUrl
                                    : value.pageUrl,
                                't',
                                'abandonedCart',
                            ),
                            lieu_nom: value.venue,
                            show_date: value.date,
                            show_title: value.title,
                        };
                    }
                }
            });
            if (Object.keys(metaData).length > 0) {
                this.addMetaData(metaData);
                this.registerEmail(this.client.email);
            }
        } catch (e) {
            console.log(e);
        }
    }

    addOrReplaceUrlParam(
        url: string,
        paramName: string,
        paramValue: string,
    ): string {
        const urlObj = new URL(url);
        const params = urlObj.searchParams;
        params.set(paramName, paramValue);
        urlObj.search = params.toString();
        return urlObj.toString();
    }

    removeFromCart(item: SymplifyItem) {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.removeFromCart(item);
        } catch (e) {
            console.log(e);
        }
    }

    removeFromCartPrice(cartPrices: [CartPrice]) {
        if (!this.active) {
            return;
        }
        try {
            cartPrices.forEach((value) => {
                const item: SymplifyItem = {
                    id: String(value.id),
                    amount:
                        value.cartDetailToken.length *
                        ((value.price + value.serviceCharges) / 100),
                    quantity: value.cartDetailToken.length,
                    description: value.name,
                    name: value.name,
                };
                this.removeFromCart(item);
            });
        } catch (e) {
            console.log(e);
        }
    }

    setCart(cartData, totalAmount) {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.setCart(cartData, totalAmount);
        } catch (e) {
            console.log(e);
        }
    }

    resetCart() {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.resetCart();
        } catch (e) {
            console.log(e);
        }
    }

    purchaseCompleted() {
        if (!this.active) {
            return;
        }
        try {
            carma.ac.purchaseCompleted();
        } catch (e) {
            console.log(e);
        }
    }
}
